/**
 * JobCentricWorkspace - Three-column layout for FOH estimate building
 * 
 * Layout:
 * LEFT (20-25%): Estimate Navigation - Job groups tree, totals
 * CENTER (55-60%): Active Job Workspace - Pricing, suggestions, actions
 * RIGHT (20-25%): Context Information - Vehicle, inspection status, send
 * 
 * Design Principles:
 * - JCE suggestions attach to the job that triggered them (not global)
 * - Advisor workflow: Select job → Review pricing → See suggestions → Add/Ignore → Next job
 * - JCE is advisory only - never blocks workflow
 */

import React, { useState, useMemo } from 'react';
import { formatVehicleNameFromObject } from '@/utils/vehicleName';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  Check, 
  AlertTriangle,
  Car,
  Send,
  Lightbulb,
  Info,
  X,
  Package,
  Clock,
  Wrench,
  Trash2,
  Edit2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// ============== LEFT PANEL: ESTIMATE NAVIGATION ==============
const EstimateNavigation = ({ 
  jobGroups, 
  selectedJobId, 
  onSelectJob, 
  onAddJobGroup,
  estimateTotal 
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };
  
  const getJobGroupTotal = (jg) => {
    const labourTotal = (jg.labour_lines || []).reduce((sum, l) => sum + (l.hours * l.rate), 0);
    const partsTotal = (jg.parts_lines || []).reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
    const chargesTotal = (jg.charges || []).reduce((sum, c) => sum + c.amount, 0);
    return labourTotal + partsTotal + chargesTotal;
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'green':
        return <Check className="w-4 h-4 text-emerald-600" />;
      case 'red':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'amber':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-slate-300" />;
    }
  };
  
  // Group jobs by category for cleaner navigation
  const groupedJobs = useMemo(() => {
    const groups = {
      'Safety Inspection': [],
      'Handling / Steering': [],
      'Suspension': [],
      'Brakes': [],
      'Electrical': [],
      'Tyres': [],
      'Diagnostic': [],
      'Other': []
    };
    
    jobGroups.forEach(jg => {
      const title = jg.title?.toLowerCase() || '';
      if (title.includes('safety') || title.includes('inspection')) {
        groups['Safety Inspection'].push(jg);
      } else if (title.includes('steering') || title.includes('tracking')) {
        groups['Handling / Steering'].push(jg);
      } else if (title.includes('suspension') || title.includes('spring') || title.includes('shock') || title.includes('mount')) {
        groups['Suspension'].push(jg);
      } else if (title.includes('brake') || title.includes('disc') || title.includes('pad')) {
        groups['Brakes'].push(jg);
      } else if (title.includes('electrical') || title.includes('battery') || title.includes('alternator')) {
        groups['Electrical'].push(jg);
      } else if (title.includes('tyre') || title.includes('tire')) {
        groups['Tyres'].push(jg);
      } else if (title.includes('diagnostic') || title.includes('fault')) {
        groups['Diagnostic'].push(jg);
      } else {
        groups['Other'].push(jg);
      }
    });
    
    return groups;
  }, [jobGroups]);
  
  return (
    <div className="h-full flex flex-col bg-slate-50 border-r border-slate-200">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-white">
        <h2 className="text-lg font-bold text-slate-800">Estimate</h2>
        <p className="text-sm text-slate-500">{jobGroups.length} job{jobGroups.length !== 1 ? 's' : ''}</p>
      </div>
      
      {/* Job Groups Tree - Scrollable */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-2">
          Job Groups
        </div>
        
        {Object.entries(groupedJobs).map(([category, jobs]) => {
          if (jobs.length === 0) return null;
          
          const isExpanded = expandedGroups[category] !== false; // Default expanded
          const categoryTotal = jobs.reduce((sum, jg) => sum + getJobGroupTotal(jg), 0);
          
          return (
            <div key={category} className="mb-1">
              {/* Category Header */}
              <button
                onClick={() => toggleGroup(category)}
                className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="font-medium text-slate-700 text-sm">{category}</span>
                  <Badge variant="outline" className="text-xs">{jobs.length}</Badge>
                </div>
                <span className="text-sm font-semibold text-slate-600">
                  £{categoryTotal.toFixed(2)}
                </span>
              </button>
              
              {/* Jobs within category */}
              {isExpanded && (
                <div className="ml-4 mt-1 space-y-1">
                  {jobs.map(jg => {
                    const isSelected = selectedJobId === jg.id;
                    const jobTotal = getJobGroupTotal(jg);
                    const isPricingConfirmed = jg.pricing_confirmed === true;
                    
                    return (
                      <button
                        key={jg.id}
                        onClick={() => onSelectJob(jg)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                          isPricingConfirmed
                            ? 'bg-emerald-100 border-l-4 border-emerald-500 hover:bg-emerald-150'
                            : isSelected 
                              ? 'bg-amber-100 border-l-4 border-amber-500' 
                              : 'hover:bg-slate-100 border-l-4 border-transparent'
                        }`}
                        data-testid={`nav-job-${jg.id}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isPricingConfirmed ? (
                            <Check className="w-4 h-4 text-emerald-600" />
                          ) : (
                            getStatusIcon(jg.status || jg.vi_status)
                          )}
                          <span className={`text-sm truncate ${
                            isPricingConfirmed 
                              ? 'font-semibold text-emerald-800' 
                              : isSelected 
                                ? 'font-semibold text-amber-900' 
                                : 'text-slate-700'
                          }`}>
                            {jg.title}
                          </span>
                        </div>
                        <span className={`text-sm font-medium ml-2 ${
                          isPricingConfirmed 
                            ? 'text-emerald-700' 
                            : isSelected 
                              ? 'text-amber-800' 
                              : 'text-slate-600'
                        }`}>
                          £{jobTotal.toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Add Job Group Button */}
        <button
          onClick={onAddJobGroup}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-4 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          data-testid="add-job-group-nav-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Add Job Group</span>
        </button>
      </div>
      
      {/* Estimate Total Footer with Progress */}
      <div className="p-4 border-t border-slate-200 bg-white">
        {/* Progress Indicator */}
        {jobGroups.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-slate-600">Pricing Progress</span>
              <span className="font-medium text-slate-700">
                {jobGroups.filter(jg => jg.pricing_confirmed).length} / {jobGroups.length} jobs
              </span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ 
                  width: `${(jobGroups.filter(jg => jg.pricing_confirmed).length / jobGroups.length) * 100}%` 
                }}
              />
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Estimate Total</span>
          <span className="text-2xl font-bold text-slate-900">
            £{estimateTotal.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============== CENTER PANEL: ACTIVE JOB WORKSPACE ==============
const ActiveJobWorkspace = ({
  selectedJob,
  suggestions,
  onUpdateJob,
  onAddLabourLine,
  onAddPartLine,
  onRemoveLine,
  onUpdateLine,
  onAddSuggestion,
  onIgnoreSuggestion,
  onConfirmPricing,
  onUnconfirmPricing,
  labourRate = 65
}) => {
  const [editingLine, setEditingLine] = useState(null);
  
  if (!selectedJob) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center p-8">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Select a Job</h3>
          <p className="text-slate-500 max-w-sm">
            Choose a job from the left panel to view and edit its details, pricing, and related work suggestions.
          </p>
        </div>
      </div>
    );
  }
  
  const labourTotal = (selectedJob.labour_lines || []).reduce((sum, l) => sum + (l.hours * l.rate), 0);
  const partsTotal = (selectedJob.parts_lines || []).reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
  const chargesTotal = (selectedJob.charges || []).reduce((sum, c) => sum + c.amount, 0);
  const jobTotal = labourTotal + partsTotal + chargesTotal;
  const isPricingConfirmed = selectedJob.pricing_confirmed === true;
  
  // Filter suggestions for this specific job
  const jobSuggestions = suggestions?.slice(0, 5) || [];
  
  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        
        {/* Job Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{selectedJob.title}</h1>
              {isPricingConfirmed && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                  <Check className="w-3 h-3 mr-1" />
                  Pricing Complete
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={selectedJob.status === 'red' ? 'destructive' : selectedJob.status === 'amber' ? 'warning' : 'default'}>
                {selectedJob.status?.toUpperCase() || 'PENDING'}
              </Badge>
              {selectedJob.source && (
                <span className="text-sm text-slate-500">Source: {selectedJob.source}</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Job Total</p>
            <p className="text-3xl font-bold text-slate-900">£{jobTotal.toFixed(2)}</p>
          </div>
        </div>
        
        {/* Parts / Labour Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wrench className="w-5 h-5 text-slate-600" />
              Parts / Labour
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Labour Lines */}
            {(selectedJob.labour_lines || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Labour</h4>
                {selectedJob.labour_lines.map((line, idx) => (
                  <div key={line.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{line.description}</p>
                      <p className="text-sm text-slate-500">{line.hours}h @ £{line.rate}/hr</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-900">£{(line.hours * line.rate).toFixed(2)}</span>
                      <button 
                        onClick={() => onRemoveLine(selectedJob.id, 'labour', line.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Parts Lines */}
            {(selectedJob.parts_lines || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Parts</h4>
                {selectedJob.parts_lines.map((part, idx) => (
                  <div key={part.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{part.description}</p>
                      <p className="text-sm text-slate-500">{part.quantity}x @ £{part.unit_price?.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-900">£{(part.quantity * part.unit_price).toFixed(2)}</span>
                      <button 
                        onClick={() => onRemoveLine(selectedJob.id, 'parts', part.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Charges */}
            {(selectedJob.charges || []).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Charges</h4>
                {selectedJob.charges.map((charge, idx) => (
                  <div key={charge.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{charge.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-900">£{charge.amount?.toFixed(2)}</span>
                      <button 
                        onClick={() => onRemoveLine(selectedJob.id, 'charges', charge.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Total */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-200">
              <span className="font-semibold text-slate-700">Total</span>
              <span className="text-xl font-bold text-slate-900">£{jobTotal.toFixed(2)}</span>
            </div>
            
            {/* Add Line Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onAddLabourLine(selectedJob.id)}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Labour
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onAddPartLine(selectedJob.id)}
                className="flex-1"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Part
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Suggested Related Work */}
        {jobSuggestions.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                <Lightbulb className="w-5 h-5" />
                Suggested Related Work
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {jobSuggestions.map((suggestion, idx) => (
                  <Button
                    key={suggestion.code || idx}
                    variant="outline"
                    size="sm"
                    onClick={() => onAddSuggestion(suggestion)}
                    className={`${idx === 0 ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : 'bg-white hover:bg-slate-50'}`}
                    data-testid={`add-suggestion-${suggestion.code}`}
                  >
                    {suggestion.label || suggestion.description}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Considerations */}
        {selectedJob.considerations && selectedJob.considerations.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-blue-600" />
                Considerations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {selectedJob.considerations.map((consideration, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-700">
                    <span className="text-blue-500 mt-0.5">•</span>
                    {consideration}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        
        {/* Default Considerations for common job types */}
        {!selectedJob.considerations && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-blue-600" />
                Considerations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-slate-600">
                {selectedJob.title?.toLowerCase().includes('spring') && (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      Suspension geometry may change after spring replacement.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      Wheel alignment is often recommended after spring replacement.
                    </li>
                  </>
                )}
                {selectedJob.title?.toLowerCase().includes('brake') && (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      Brake fluid condition should be checked during brake work.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      New pads require careful bedding-in during initial driving.
                    </li>
                  </>
                )}
                {selectedJob.title?.toLowerCase().includes('tyre') && (
                  <>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      Wheel alignment recommended after tyre replacement.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      TPMS sensor may need resetting after tyre change.
                    </li>
                  </>
                )}
                {!selectedJob.title?.toLowerCase().includes('spring') && 
                 !selectedJob.title?.toLowerCase().includes('brake') && 
                 !selectedJob.title?.toLowerCase().includes('tyre') && (
                  <li className="flex items-start gap-2 text-slate-500 italic">
                    <span className="text-slate-400 mt-0.5">•</span>
                    No specific considerations for this job type.
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}
        
        {/* Actions */}
        {jobSuggestions.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {jobSuggestions.slice(0, 2).map((suggestion, idx) => (
                  <Button
                    key={suggestion.code || idx}
                    onClick={() => onAddSuggestion(suggestion)}
                    className={idx === 0 ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    variant={idx === 0 ? 'default' : 'outline'}
                  >
                    Add {suggestion.label || suggestion.description}
                  </Button>
                ))}
                <Button 
                  variant="ghost" 
                  onClick={() => onIgnoreSuggestion(selectedJob.id)}
                  className="text-slate-500"
                >
                  Ignore Suggestions
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Confirm Pricing Complete Button */}
        <div className="pt-4 border-t border-slate-200">
          {isPricingConfirmed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Pricing confirmed for this job</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onUnconfirmPricing(selectedJob.id)}
                className="text-slate-600"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit Pricing
              </Button>
            </div>
          ) : (
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
              onClick={() => onConfirmPricing(selectedJob.id)}
              data-testid="confirm-pricing-btn"
            >
              <Check className="w-5 h-5 mr-2" />
              Confirm Pricing Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== RIGHT PANEL: CONTEXT INFORMATION ==============
const ContextPanel = ({
  vehicle,
  inspection,
  estimateTotal,
  itemCount,
  onSend,
  isSending,
  smsReady,
  emailReady
}) => {
  return (
    <div className="h-full flex flex-col bg-slate-50 border-l border-slate-200">
      {/* Vehicle Section */}
      <div className="p-4 border-b border-slate-200 bg-white">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Vehicle</h3>
        
        <div className="space-y-3">
          {/* Vehicle Image Placeholder */}
          {vehicle?.image_url ? (
            <img 
              src={vehicle.image_url} 
              alt={formatVehicleNameFromObject(vehicle)}
              className="w-full h-24 object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center">
              <Car className="w-12 h-12 text-slate-300" />
            </div>
          )}
          
          {/* Vehicle Details */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Registration</span>
              <span className="font-bold text-lg text-slate-900">{vehicle?.reg || 'N/A'}</span>
            </div>
            {formatVehicleNameFromObject(vehicle) && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Vehicle</span>
                <span className="font-medium text-slate-700">{formatVehicleNameFromObject(vehicle)}</span>
              </div>
            )}
            {vehicle?.mileage && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Mileage</span>
                <span className="font-medium text-slate-700">{vehicle.mileage.toLocaleString()} miles</span>
              </div>
            )}
            {inspection?.date && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Inspected</span>
                <span className="text-sm text-slate-700">{new Date(inspection.date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Inspection Status */}
      <div className="p-4 border-b border-slate-200 bg-white">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Inspection</h3>
        
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-5 h-5 text-emerald-500" />
          <span className="font-medium text-emerald-700">{inspection?.status || 'Complete'}</span>
        </div>
      </div>
      
      {/* Send State */}
      <div className="p-4 border-b border-slate-200 bg-white flex-1">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Send State</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">SMS</span>
            <div className="flex items-center gap-1">
              {smsReady ? (
                <><Check className="w-4 h-4 text-emerald-500" /><span className="text-emerald-600 text-sm">Ready</span></>
              ) : (
                <span className="text-amber-600 text-sm">Pending</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Email</span>
            <div className="flex items-center gap-1">
              {emailReady ? (
                <><Check className="w-4 h-4 text-emerald-500" /><span className="text-emerald-600 text-sm">Ready</span></>
              ) : (
                <span className="text-amber-600 text-sm">Pending</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-slate-600">Estimate</span>
            <span className="font-bold text-lg text-slate-900">£{estimateTotal.toFixed(2)}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Items</span>
            <span className="font-medium text-slate-700">{itemCount}</span>
          </div>
        </div>
      </div>
      
      {/* Send Button */}
      <div className="p-4 bg-white">
        <Button 
          className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6"
          onClick={onSend}
          disabled={isSending}
          data-testid="send-to-customer-btn"
        >
          <Send className="w-5 h-5 mr-2" />
          {isSending ? 'Sending...' : 'Send to Customer'}
        </Button>
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT: THREE-COLUMN LAYOUT ==============
const JobCentricWorkspace = ({
  jobGroups,
  setJobGroups,
  vehicleData,
  inspectionData,
  suggestions,
  onSave,
  onSend,
  isSaving,
  isSending,
  smsReady,
  emailReady,
  onAddJobGroup,
  labourRate = 65
}) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [ignoredSuggestions, setIgnoredSuggestions] = useState({});
  
  // Calculate estimate total
  const estimateTotal = useMemo(() => {
    return jobGroups.reduce((total, jg) => {
      const labourTotal = (jg.labour_lines || []).reduce((sum, l) => sum + (l.hours * l.rate), 0);
      const partsTotal = (jg.parts_lines || []).reduce((sum, p) => sum + (p.quantity * p.unit_price), 0);
      const chargesTotal = (jg.charges || []).reduce((sum, c) => sum + c.amount, 0);
      return total + labourTotal + partsTotal + chargesTotal;
    }, 0);
  }, [jobGroups]);
  
  // Calculate total item count
  const itemCount = useMemo(() => {
    return jobGroups.reduce((count, jg) => {
      return count + 
        (jg.labour_lines || []).length + 
        (jg.parts_lines || []).length + 
        (jg.charges || []).length;
    }, 0);
  }, [jobGroups]);
  
  // Get suggestions for the selected job
  const jobSuggestions = useMemo(() => {
    if (!selectedJob || !suggestions) return [];
    
    // Filter suggestions that are relevant to the selected job
    // Match by triggered_by, repair_intent, or job title similarity
    const jobTitle = selectedJob.title?.toLowerCase() || '';
    const jobKey = selectedJob.vi_item_key || selectedJob.repair_intent_code;
    
    return suggestions.filter(s => {
      // Skip if already ignored for this job
      if (ignoredSuggestions[selectedJob.id]?.includes(s.code)) return false;
      
      // Match by triggered_by field
      if (s.triggered_by === jobKey) return true;
      
      // Match by job title keywords
      const suggestionLabel = (s.label || s.description || '').toLowerCase();
      
      // Common associations
      if (jobTitle.includes('spring') && 
          (suggestionLabel.includes('alignment') || suggestionLabel.includes('mount') || suggestionLabel.includes('drop link'))) {
        return true;
      }
      if (jobTitle.includes('brake') && 
          (suggestionLabel.includes('fluid') || suggestionLabel.includes('sensor'))) {
        return true;
      }
      if (jobTitle.includes('tyre') && 
          (suggestionLabel.includes('alignment') || suggestionLabel.includes('balance'))) {
        return true;
      }
      
      return false;
    }).slice(0, 5);
  }, [selectedJob, suggestions, ignoredSuggestions]);
  
  const handleSelectJob = (job) => {
    setSelectedJob(job);
  };
  
  const handleAddLabourLine = (jobId) => {
    const newLine = {
      id: `labour-${Date.now()}`,
      category: 'strip_renew',
      description: 'New Labour Item',
      hours: 1,
      rate: labourRate
    };
    
    setJobGroups(prev => prev.map(jg => {
      if (jg.id === jobId) {
        return {
          ...jg,
          labour_lines: [...(jg.labour_lines || []), newLine]
        };
      }
      return jg;
    }));
  };
  
  const handleAddPartLine = (jobId) => {
    const newPart = {
      id: `part-${Date.now()}`,
      description: 'New Part',
      quantity: 1,
      unit_price: 0,
      vat_rate: 20
    };
    
    setJobGroups(prev => prev.map(jg => {
      if (jg.id === jobId) {
        return {
          ...jg,
          parts_lines: [...(jg.parts_lines || []), newPart]
        };
      }
      return jg;
    }));
  };
  
  const handleRemoveLine = (jobId, lineType, lineId) => {
    setJobGroups(prev => prev.map(jg => {
      if (jg.id === jobId) {
        const key = lineType === 'labour' ? 'labour_lines' : lineType === 'parts' ? 'parts_lines' : 'charges';
        return {
          ...jg,
          [key]: (jg[key] || []).filter(line => line.id !== lineId)
        };
      }
      return jg;
    }));
  };
  
  const handleUpdateLine = (jobId, lineType, lineId, updates) => {
    setJobGroups(prev => prev.map(jg => {
      if (jg.id === jobId) {
        const key = lineType === 'labour' ? 'labour_lines' : lineType === 'parts' ? 'parts_lines' : 'charges';
        return {
          ...jg,
          [key]: (jg[key] || []).map(line => 
            line.id === lineId ? { ...line, ...updates } : line
          )
        };
      }
      return jg;
    }));
  };
  
  const handleAddSuggestion = (suggestion) => {
    if (!selectedJob) return;
    
    // Determine what type of line to add
    if (suggestion.type === 'labour' || suggestion.type === 'service' || !suggestion.estimated_part_cost) {
      const newLine = {
        id: `labour-jce-${Date.now()}`,
        category: 'strip_renew',
        description: suggestion.label || suggestion.description,
        hours: suggestion.estimated_labour_hours || 0.5,
        rate: labourRate,
        is_jce_suggestion: true,
        jce_code: suggestion.code
      };
      
      setJobGroups(prev => prev.map(jg => {
        if (jg.id === selectedJob.id) {
          return {
            ...jg,
            labour_lines: [...(jg.labour_lines || []), newLine]
          };
        }
        return jg;
      }));
    } else {
      const newPart = {
        id: `part-jce-${Date.now()}`,
        description: suggestion.label || suggestion.description,
        quantity: 1,
        unit_price: suggestion.estimated_part_cost || 0,
        vat_rate: 20,
        is_jce_suggestion: true,
        jce_code: suggestion.code
      };
      
      setJobGroups(prev => prev.map(jg => {
        if (jg.id === selectedJob.id) {
          return {
            ...jg,
            parts_lines: [...(jg.parts_lines || []), newPart]
          };
        }
        return jg;
      }));
    }
    
    // Update selected job reference
    setSelectedJob(prev => {
      const updated = jobGroups.find(jg => jg.id === prev.id);
      return updated || prev;
    });
  };
  
  const handleIgnoreSuggestion = (jobId) => {
    // Mark all current suggestions as ignored for this job
    const currentCodes = jobSuggestions.map(s => s.code);
    setIgnoredSuggestions(prev => ({
      ...prev,
      [jobId]: [...(prev[jobId] || []), ...currentCodes]
    }));
  };
  
  const handleUpdateJob = (jobId, updates) => {
    setJobGroups(prev => prev.map(jg => 
      jg.id === jobId ? { ...jg, ...updates } : jg
    ));
  };
  
  // Confirm pricing for a job - turns it green in navigation
  const handleConfirmPricing = (jobId) => {
    setJobGroups(prev => prev.map(jg => 
      jg.id === jobId ? { ...jg, pricing_confirmed: true } : jg
    ));
  };
  
  // Unconfirm pricing - allows editing again
  const handleUnconfirmPricing = (jobId) => {
    setJobGroups(prev => prev.map(jg => 
      jg.id === jobId ? { ...jg, pricing_confirmed: false } : jg
    ));
  };
  
  // Sync selectedJob when jobGroups change
  React.useEffect(() => {
    if (selectedJob) {
      const updated = jobGroups.find(jg => jg.id === selectedJob.id);
      if (updated) {
        setSelectedJob(updated);
      }
    }
  }, [jobGroups]);
  
  return (
    <div className="h-screen flex bg-slate-100" data-testid="job-centric-workspace">
      {/* LEFT PANEL - Estimate Navigation */}
      <div className="w-1/5 min-w-[250px] max-w-[320px]">
        <EstimateNavigation
          jobGroups={jobGroups}
          selectedJobId={selectedJob?.id}
          onSelectJob={handleSelectJob}
          onAddJobGroup={onAddJobGroup}
          estimateTotal={estimateTotal}
        />
      </div>
      
      {/* CENTER PANEL - Active Job Workspace */}
      <div className="flex-1">
        <ActiveJobWorkspace
          selectedJob={selectedJob}
          suggestions={jobSuggestions}
          onUpdateJob={handleUpdateJob}
          onAddLabourLine={handleAddLabourLine}
          onAddPartLine={handleAddPartLine}
          onRemoveLine={handleRemoveLine}
          onUpdateLine={handleUpdateLine}
          onAddSuggestion={handleAddSuggestion}
          onIgnoreSuggestion={handleIgnoreSuggestion}
          onConfirmPricing={handleConfirmPricing}
          onUnconfirmPricing={handleUnconfirmPricing}
          labourRate={labourRate}
        />
      </div>
      
      {/* RIGHT PANEL - Context Information */}
      <div className="w-1/5 min-w-[250px] max-w-[320px]">
        <ContextPanel
          vehicle={vehicleData}
          inspection={inspectionData}
          estimateTotal={estimateTotal}
          itemCount={itemCount}
          onSend={onSend}
          isSending={isSending}
          smsReady={smsReady}
          emailReady={emailReady}
        />
      </div>
    </div>
  );
};

export default JobCentricWorkspace;
export { EstimateNavigation, ActiveJobWorkspace, ContextPanel };
